import { MOTION, SHADOW } from "@elyxeer/ui";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import { ArticleCover } from "~/components/news/ArticleCover";
import type { DashboardHighlightSummary } from "~/types/dashboard-highlight";

/** The single admin-selected story placed between the upcoming event and news. */
export function DashboardHighlightCard({
  highlight,
}: {
  highlight: DashboardHighlightSummary;
}) {
  const { t } = useTranslation();

  return (
    <Card
      variant="outlined"
      data-testid="highlights.card"
      sx={{
        transition: `box-shadow ${MOTION.transition}, transform ${MOTION.transition}`,
        "&:hover": { boxShadow: SHADOW.raised, transform: "translateY(-2px)" },
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={`/highlights/${highlight.slug}`}
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "stretch",
        }}
      >
        <ArticleCover
          slug={highlight.slug}
          src={highlight.cover_image_url}
          alt=""
          sx={{
            borderRadius: 0,
            flexShrink: 0,
            width: { xs: "100%", md: "46%" },
            alignSelf: { md: "center" },
          }}
        />

        <Box
          sx={{
            display: "flex",
            flex: 1,
            flexDirection: "column",
            justifyContent: "center",
            gap: 1.25,
            padding: { xs: 2.5, md: 4 },
          }}
        >
          <Typography variant="H3" sx={{ color: "text.primary" }}>
            {highlight.title}
          </Typography>

          <Typography
            variant="T1"
            sx={{
              color: "text.secondary",
              display: "-webkit-box",
              WebkitLineClamp: 4,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {highlight.summary}
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              color: "link.main",
              marginTop: 0.5,
            }}
          >
            <Typography variant="L2" sx={{ color: "inherit" }}>
              {t("highlights.open")}
            </Typography>
            <ArrowForwardIcon sx={{ fontSize: "1rem" }} />
          </Box>
        </Box>
      </CardActionArea>
    </Card>
  );
}
